'use client';

import { useEffect, useState } from 'react';
import { PrismicImage } from '@prismicio/react';
import { X } from 'lucide-react';
import { MarqueeCardsRow } from '@/components/redesign/StackedHeaderCardsRow/MarqueeCardsRow';
import { Article } from './ArticleView';

interface Props {
  items: Article['data']['media_items'];
}

export function ArticleImageRibbon({ items }: Props) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    if (activeIndex === null) {
      return;
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveIndex(null);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [activeIndex]);

  if (!items || items.length === 0) {
    return null;
  }

  const activeImage = activeIndex !== null ? items[activeIndex]?.media : null;

  return (
    <section className="w-full bg-white px-5 pt-8 md:px-10 md:pt-10 lg:px-20">
      <div className="mx-auto w-full max-w-[1280px]">
        <MarqueeCardsRow speed={40} gap={0}>
          {items.map((item, idx) => (
            <button
              key={`ribbon-${idx}`}
              type="button"
              onClick={() => setActiveIndex(idx)}
              aria-label="Open image"
              className="relative mx-2 h-[207px] w-[322px] shrink-0 overflow-hidden rounded-[16px] md:mx-3 md:h-[276px] md:w-[437px]"
            >
              <PrismicImage
                field={item.media}
                imgixParams={{ w: 874, h: 552, fit: 'crop' }}
                className="h-full w-full object-cover object-center"
              />
            </button>
          ))}
        </MarqueeCardsRow>
      </div>

      {activeImage?.url && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setActiveIndex(null)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-6"
        >
          <button
            type="button"
            onClick={() => setActiveIndex(null)}
            aria-label="Close"
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full text-white outline-none hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/60"
          >
            <X className="h-6 w-6" strokeWidth={2} />
          </button>
          {/* Plain <img> so the browser renders the asset at its natural
              dimensions (capped to viewport by max-w/max-h). PrismicImage would
              apply imgix sizing params we don't want in the lightbox. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={activeImage.url}
            alt={activeImage.alt ?? ''}
            className="h-auto max-h-[90vh] w-auto max-w-[90vw]"
          />
        </div>
      )}
    </section>
  );
}
